<template>
  <div class="pivot-app">
    <h5>📊 Tableau Croisé Dynamique</h5>
    
    <!-- Configuration -->
    <div class="config-panel">
      <div class="config-section">
        <h6>⚙️ Configuration</h6>
        
        <div class="config-grid">
          <div class="config-box">
            <label>Lignes :</label>
            <div class="selected-items">
              <div 
                v-for="(field, index) in config.rows" 
                :key="field"
                class="selected-item"
                draggable="true"
                @dragstart="dragStart($event, index)"
                @dragover.prevent
                @drop="dropOnRow($event, index)"
              >
                <span>{{ formatHeader(field) }}</span>
                <button @click="removeRow(index)" class="remove-btn">×</button>
              </div>
            </div>
            <select @change="addRow" class="add-select">
              <option value="">Ajouter un champ...</option>
              <option 
                v-for="field in availableRows" 
                :key="field" 
                :value="field"
              >
                {{ formatHeader(field) }}
              </option>
            </select>
          </div>
          
          <div class="config-box">
            <label>Colonnes :</label>
            <div class="selected-items">
              <div 
                v-for="(field, index) in config.cols" 
                :key="field"
                class="selected-item"
                draggable="true"
                @dragstart="dragStart($event, index)"
                @dragover.prevent
                @drop="dropOnCol($event, index)"
              >
                <span>{{ formatHeader(field) }}</span>
                <button @click="removeCol(index)" class="remove-btn">×</button>
              </div>
            </div>
            <select @change="addCol" class="add-select">
              <option value="">Ajouter un champ...</option>
              <option 
                v-for="field in availableCols" 
                :key="field" 
                :value="field"
              >
                {{ formatHeader(field) }}
              </option>
            </select>
          </div>
          
          <div class="config-box">
            <label>Valeurs :</label>
            <select v-model="config.vals" @change="calculatePivot">
              <option v-for="field in numericFields" :key="field" :value="field">
                {{ formatHeader(field) }}
              </option>
            </select>
            
            <label>Agrégateur :</label>
            <select v-model="config.aggregator" @change="calculatePivot">
              <option value="sum">Somme</option>
              <option value="avg">Moyenne</option>
              <option value="count">Compte</option>
            </select>
          </div>
        </div>
        
        <div class="options">
          <label>
            <input type="checkbox" v-model="config.showTotals" @change="calculatePivot">
            Afficher les totaux
          </label>
          <label>
            <input type="checkbox" v-model="config.showSubtotals" @change="calculatePivot">
            Afficher les sous-totaux
          </label>
        </div>
        
        <div class="action-buttons">
          <button @click="loadExample" class="btn-primary">
            📋 Exemple Excel
          </button>
          <button @click="resetConfig" class="btn-secondary">
            🔄 Réinitialiser
          </button>
        </div>
      </div>
    </div>
    
    <!-- Tableau croisé - SANS TRI -->
    <div class="pivot-results">
      <div class="results-header">
        <h5>📈 Résultats</h5>
        <div class="stats">
          <span>{{ displayRows.length }} lignes</span>
          <span>Total: {{ formatNumber(grandTotal) }}</span>
        </div>
      </div>
      
      <!-- TABLEAU SANS TRI -->
      <div class="table-container" v-if="displayRows.length > 0 && config.vals">
        <div class="table-scroll">
          <table class="pivot-table">
            <!-- En-têtes -->
            <thead>
              <tr>
                <!-- En-têtes des lignes -->
                <th 
                  v-for="(rowField, index) in config.rows" 
                  :key="'row-header-' + index"
                  class="row-header"
                >
                  {{ formatHeader(rowField) }}
                </th>
                
                <!-- En-têtes des colonnes -->
                <template v-if="config.cols.length > 0">
                  <th v-if="false"
                    v-for="(colValue, colIndex) in columnValues" 
                    :key="'col-header-' + colIndex"
                    class="col-header"
                  >
                    {{ colValue }}
                  </th>
                </template>
                <template v-else>
                  <th class="value-header">
                    {{ formatHeader(config.vals) }}
                  </th>
                </template>
                
                <!-- Colonne Total -->
                <th v-if="config.showTotals" class="total-header">
                  Total
                </th>
              </tr>
            </thead>
            
            <!-- Corps du tableau -->
            <tbody>
              <!-- Lignes de données DANS L'ORDRE NATUREL -->
              <tr 
                v-for="(row, rowIndex) in displayRows" 
                :key="'row-' + rowIndex"
                :class="getRowClass(row)"
              >
                <!-- Valeurs des champs de lignes -->
                <td 
                  v-for="(fieldValue, fieldIndex) in row.path" 
                  :key="'field-' + fieldIndex"
                  class="row-cell"
                  :style="{ paddingLeft: (fieldIndex * 20) + 'px' }"
                >
                  {{ fieldValue }}
                  <span v-if="row.isSubtotal" class="subtotal-badge">
                    (sous-total)
                  </span>
                </td>
                
                <!-- Cellules vides pour les champs manquants -->
                <td 
                  v-if="row.path.length < config.rows.length"
                  :colspan="config.rows.length - row.path.length"
                ></td>
                
                <!-- Données -->
                <template v-if="config.cols.length > 0">
                  <td v-if="false"
                    v-for="(colValue, colIndex) in columnValues" 
                    :key="'data-' + colIndex"
                    class="data-cell numeric"
                  >
                    {{ formatNumber(getCellValue(row, colValue)) }}
                  </td>
                </template>
                <template v-else>
                  <td class="data-cell numeric">
                    {{ formatNumber(getRowTotal(row)) }}
                  </td>
                </template>
                
                <!-- Total de la ligne -->
                <td v-if="config.showTotals" class="total-cell numeric">
                  {{ formatNumber(getRowTotal(row)) }}
                </td>
              </tr>
              
              <!-- Total général -->
              <tr v-if="config.showTotals" class="grand-total-row">
                <td :colspan="config.rows.length" class="grand-total-label">
                  TOTAL GÉNÉRAL
                </td>
                
                <template v-if="config.cols.length > 0">
                  <td  v-if="false"
                    v-for="(colValue, colIndex) in columnValues" 
                    :key="'gt-col-' + colIndex"
                    class="grand-total-cell numeric"
                  >
                    {{ formatNumber(getColumnTotal(colValue)) }}
                  </td>
                </template>
                <template v-else>
                  <td class="grand-total-cell numeric">
                    {{ formatNumber(grandTotal) }}
                  </td>
                </template>
                
                <td class="grand-total-cell numeric">
                  {{ formatNumber(grandTotal) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      
      <!-- État vide -->
      <div v-else class="empty-state">
        <p>⚙️ Configurez les champs pour générer le tableau</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, watch, onMounted } from 'vue'
import axios from 'axios'
// Données d'exemple
let sampleData = ref([
  { region: 'Nord', produit: 'Ordinateur', mois: 'Janvier', ventes: 50000, quantite: 50 },
  { region: 'Nord', produit: 'Ordinateur', mois: 'Février', ventes: 48000, quantite: 48 },
  { region: 'Nord', produit: 'Téléphone', mois: 'Janvier', ventes: 60000, quantite: 120 },
  { region: 'Nord', produit: 'Téléphone', mois: 'Février', ventes: 55000, quantite: 110 },
  { region: 'Sud', produit: 'Ordinateur', mois: 'Janvier', ventes: 45000, quantite: 45 },
  { region: 'Sud', produit: 'Ordinateur', mois: 'Février', ventes: 42000, quantite: 42 },
  { region: 'Sud', produit: 'Téléphone', mois: 'Janvier', ventes: 55000, quantite: 110 },
  { region: 'Sud', produit: 'Téléphone', mois: 'Février', ventes: 50000, quantite: 100 },
  { region: 'Est', produit: 'Ordinateur', mois: 'Janvier', ventes: 35000, quantite: 35 },
  { region: 'Est', produit: 'Téléphone', mois: 'Janvier', ventes: 40000, quantite: 80 },
])

// Configuration
const config = reactive({
  rows: ['region', 'produit'],
  cols: ['mois'],
  vals: 'ventes',
  aggregator: 'sum',
  showTotals: true,
  showSubtotals: true
})

// Données calculées
const displayRows = ref([])
const columnValues = ref([])
const rowTotals = ref({})
const columnTotals = ref({})
const matrix = ref({})
const grandTotal = ref(0)
const draggedIndex = ref(null)

// Computed
const allFields = computed(() => {
  return sampleData.value.length > 0 
    ? Object.keys(sampleData.value[0]) 
    : []
})

const numericFields = computed(() => {
  if (sampleData.value.length === 0) return []
  const firstItem = sampleData.value[0]
  return Object.keys(firstItem).filter(key => 
    typeof firstItem[key] === 'number'
  )
})

const availableRows = computed(() => {
  const usedFields = [...config.rows, ...config.cols, config.vals].filter(Boolean)
  return allFields.value.filter(field => !usedFields.includes(field))
})

const availableCols = computed(() => {
  const usedFields = [...config.rows, ...config.cols, config.vals].filter(Boolean)
  return allFields.value.filter(field => !usedFields.includes(field))
})

// Initialisation
onMounted(() => {
  calculatePivot()
  getStats('statistiques_ventes_tiers_etablissement',1)
})

// Calcul du pivot - SANS TRI
const calculatePivot = () => {
  if (!config.vals || config.rows.length === 0) {
    resetData()
    return
  }
  
  // Réinitialiser
  resetData()
  
  // 1. Extraire les valeurs uniques des colonnes
  if (config.cols.length > 0) {
    const uniqueCols = new Set()
    sampleData.value.forEach(item => {
      const colKey = config.cols
        .map(field => item[field] || '')
        .filter(v => v !== '')
        .join('|')
      if (colKey) uniqueCols.add(colKey)
    })
    columnValues.value = Array.from(uniqueCols).sort()
  }
  
  // 2. Grouper les données - conservation de l'ordre d'apparition
  const rowMap = new Map()
  const rowOrder = [] // Pour conserver l'ordre d'apparition
  const tempMatrix = {}
  
  sampleData.value.forEach(item => {
    // Clé de ligne
    const rowKey = config.rows
      .map(field => item[field] || '')
      .filter(v => v !== '')
      .join('|')
    
    if (!rowKey) return
    
    // Clé de colonne
    const colKey = config.cols.length > 0
      ? config.cols
          .map(field => item[field] || '')
          .filter(v => v !== '')
          .join('|')
      : 'value'
    
    // Valeur
    const value = Number(item[config.vals]) || 0
    
    // Stocker la ligne (première apparition détermine l'ordre)
    if (!rowMap.has(rowKey)) {
      const row = {
        key: rowKey,
        path: rowKey.split('|'),
        isSubtotal: false
      }
      rowMap.set(rowKey, row)
      rowOrder.push(row)
    }
    
    // Stocker dans la matrice
    const matrixKey = `${rowKey}|${colKey}`
    if (!tempMatrix[matrixKey]) {
      tempMatrix[matrixKey] = 0
    }
    
    // Agrégation
    switch(config.aggregator) {
      case 'sum':
        tempMatrix[matrixKey] += value
        break
      case 'avg':
        tempMatrix[matrixKey] += value
        break
      case 'count':
        tempMatrix[matrixKey] += 1
        break
    }
  })
  
  matrix.value = tempMatrix
  
  // 3. Préparer les lignes d'affichage - DANS L'ORDRE NATUREL
  const rows = [...rowOrder] // Conserver l'ordre d'apparition
  
  // Ajouter les sous-totaux si demandé
  if (config.showSubtotals && config.rows.length > 1) {
    const subtotalRows = []
    
    rows.forEach(row => {
      for (let i = 1; i < row.path.length; i++) {
        const subtotalPath = row.path.slice(0, i)
        const subtotalKey = subtotalPath.join('|')
        
        // Vérifier si ce sous-total n'existe pas déjà
        const existingSubtotal = subtotalRows.find(r => r.key === subtotalKey)
        if (!existingSubtotal) {
          subtotalRows.push({
            key: subtotalKey,
            path: subtotalPath,
            isSubtotal: true,
            // Insérer après la dernière ligne du même groupe
            insertAfter: rows.filter(r => 
              r.path.slice(0, i).join('|') === subtotalKey
            ).pop()
          })
        }
      }
    })
    
    // Insérer les sous-totaux à leur position correcte
    subtotalRows.forEach(subtotal => {
      const insertIndex = rows.findIndex(r => r === subtotal.insertAfter) + 1
      if (insertIndex > 0) {
        rows.splice(insertIndex, 0, {
          key: subtotal.key,
          path: subtotal.path,
          isSubtotal: true
        })
      }
    })
  }
  
  // 4. Trier naturellement par hiérarchie (sans tri par total)
  rows.sort((a, b) => {
    // Pour la même hiérarchie, garder l'ordre d'apparition
    for (let i = 0; i < Math.min(a.path.length, b.path.length); i++) {
      if (a.path[i] !== b.path[i]) {
        return a.path[i].localeCompare(b.path[i])
      }
    }
    return a.path.length - b.path.length
  })
  
  displayRows.value = rows
  
  // 5. Calculer les totaux
  calculateTotals()
}

const calculateTotals = () => {
  // Réinitialiser les totaux
  rowTotals.value = {}
  columnTotals.value = {}
  grandTotal.value = 0
  
  // Calculer les totaux par ligne
  displayRows.value.forEach(row => {
    const rowKey = row.key
    let total = 0
    
    columnValues.value.forEach(colKey => {
      const matrixKey = `${rowKey}|${colKey}`
      total += matrix.value[matrixKey] || 0
    })
    
    rowTotals.value[rowKey] = total
  })
  
  // Calculer les totaux par colonne
  columnValues.value.forEach(colKey => {
    let total = 0
    
    displayRows.value.forEach(row => {
      const matrixKey = `${row.key}|${colKey}`
      total += matrix.value[matrixKey] || 0
    })
    
    columnTotals.value[colKey] = total
    grandTotal.value += total
  })
}

const getCellValue = (row, colKey) => {
  const matrixKey = `${row.key}|${colKey}`
  return matrix.value[matrixKey] || 0
}

const getRowTotal = (row) => {
  return rowTotals.value[row.key] || 0
}

const getColumnTotal = (colKey) => {
  return columnTotals.value[colKey] || 0
}

const getRowClass = (row) => {
  return row.isSubtotal ? 'subtotal-row' : 'data-row'
}

const resetData = () => {
  displayRows.value = []
  columnValues.value = []
  rowTotals.value = {}
  columnTotals.value = {}
  matrix.value = {}
  grandTotal.value = 0
}

// Gestion de l'ordre des lignes
const addRow = (event) => {
  const field = event.target.value
  if (field && !config.rows.includes(field)) {
    config.rows.push(field)
    event.target.value = ''
    calculatePivot()
  }
}

const removeRow = (index) => {
  config.rows.splice(index, 1)
  calculatePivot()
}

const addCol = (event) => {
  const field = event.target.value
  if (field && !config.cols.includes(field)) {
    config.cols.push(field)
    event.target.value = ''
    calculatePivot()
  }
}

const removeCol = (index) => {
  config.cols.splice(index, 1)
  calculatePivot()
}

// Drag & drop pour réordonner
const dragStart = (event, index) => {
  draggedIndex.value = index
  event.dataTransfer.effectAllowed = 'move'
}

const dropOnRow = (event, dropIndex) => {
  if (draggedIndex.value === null) return
  
  const items = [...config.rows]
  const draggedItem = items[draggedIndex.value]
  items.splice(draggedIndex.value, 1)
  items.splice(dropIndex, 0, draggedItem)
  
  config.rows = items
  draggedIndex.value = null
  calculatePivot()
}

const dropOnCol = (event, dropIndex) => {
  if (draggedIndex.value === null) return
  
  const items = [...config.cols]
  const draggedItem = items[draggedIndex.value]
  items.splice(draggedIndex.value, 1)
  items.splice(dropIndex, 0, draggedItem)
  
  config.cols = items
  draggedIndex.value = null
  calculatePivot()
}

const formatHeader = (field) => {
  const headers = {
    region: 'Région',
    produit: 'Produit',
    mois: 'Mois',
    ventes: 'Ventes',
    quantite: 'Quantité'
  }
  return headers[field] || field.charAt(0).toUpperCase() + field.slice(1)
}

const formatNumber = (num) => {
  if (typeof num !== 'number' || isNaN(num)) return '-'
  
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + 'M'
  } else if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'K'
  }
  
  return Math.round(num).toLocaleString('fr-FR')
}

const loadExample = () => {
  config.rows = ['region', 'produit']
  config.cols = ['mois']
  config.vals = 'ventes'
  config.aggregator = 'sum'
  config.showTotals = true
  config.showSubtotals = true
}

const resetConfig = () => {
  config.rows = []
  config.cols = []
  config.vals = numericFields.value[0] || ''
  config.aggregator = 'sum'
  config.showTotals = true
  config.showSubtotals = false
}


let filtreParams=ref({
        date_debut:'2025-10-01' ,
        date_fin:'2025-10-31' ,
        date_debut_exclu: null ,
        date_fin_exclu: null ,
        gp_tiers:[] ,
        gl_etablissement: [] ,
        code_acheteur: null ,
        codepole: null ,
        ga_libreart1: null ,
        code_theme:null ,
        ga_familleniv1: null ,
        ga_familleniv2: null ,
        ga_familleniv3: null ,
        ga_codearticle:null ,
        codebarre: null ,
        cab_secondaire: null ,
        gl_collection: null ,
        motif_remise: null,
        numTel: null,
        email: null,
        orderby: null,
        limit : 50
})
let statsTableFiltred=ref([])
let statsTable=ref([])

async function getStats(type,page = 1){
    // dialogSpin.value=true
    let params = {
        ...filtreParams.value,
        filliale: null,
        codepole: filtreParams.value.codepole?filtreParams.value.codepole.map(x=>x=x.code):null  ,
        ga_libreart1: filtreParams.value.ga_libreart1?filtreParams.value.ga_libreart1.map(x=>x=x.split("-")[0].trim()):null  ,
        code_theme:filtreParams.value.code_theme?filtreParams.value.code_theme.map(x=>x=x.split("-")[0].trim()):null  ,
        ga_familleniv1: filtreParams.value.ga_familleniv1?filtreParams.value.ga_familleniv1.map(x=>x=x.split('-')[0].trim()):null  ,
        ga_familleniv2: filtreParams.value.ga_familleniv2?filtreParams.value.ga_familleniv2.map(x=>x=x.split('-')[0].trim()):null  ,
        ga_familleniv3: filtreParams.value.ga_familleniv3?filtreParams.value.ga_familleniv3.map(x=>x=x.split('-')[0].trim()):null ,
        numTel: filtreParams.value.numTel?true:null,
        email: filtreParams.value.email?true:null
    }
  //console.log(params);
  
  let url = `https://my.edisac.com/api/statistics/${type}?page=${page}`
  let config = {
    method: 'get',
    url: url,
    params: params
  }
  
  return axios.request(config)
    .then((response) => { 
     
        statsTable.value =response.data.tiersTable
        statsTableFiltred.value=statsTable.value.slice(0, 50)
        sampleData.value=statsTable.value.slice(0, 50)

        //console.log(sampleData.value);
        
    })
    .catch((error) => {
      //console.log(error)
   
    })
}
</script>

<style scoped>
.pivot-app {
  font-family: 'Segoe UI', Arial, sans-serif;
  padding: 20px;
  max-width: 1400px;
  margin: 0 auto;
}

h2 {
  color: #2c3e50;
  margin-bottom: 20px;
  border-bottom: 2px solid #3498db;
  padding-bottom: 10px;
}

/* Configuration */
.config-panel {
  background: white;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  border: 1px solid #dee2e6;
}

.config-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-bottom: 20px;
}

.config-box {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.config-box label {
  font-weight: 600;
  color: #495057;
}

.selected-items {
  min-height: 80px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  padding: 10px;
  background: #f8f9fa;
}

.selected-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: white;
  padding: 6px 10px;
  margin-bottom: 5px;
  border-radius: 3px;
  border: 1px solid #dee2e6;
  cursor: move;
}

.selected-item:hover {
  background: #e9ecef;
}

.remove-btn {
  background: #dc3545;
  color: white;
  border: none;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  cursor: pointer;
  font-size: 14px;
  line-height: 1;
}

.add-select {
  padding: 8px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  background: white;
}

.options {
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
}

.options label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.action-buttons {
  display: flex;
  gap: 10px;
}

/* Tableau */
.pivot-results {
  background: white;
  border-radius: 8px;
  padding: 20px;
  border: 1px solid #dee2e6;
}

.results-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.stats {
  color: #6c757d;
}

.table-container {
  border: 1px solid #dee2e6;
  border-radius: 4px;
  overflow: hidden;
}

.table-scroll {
  overflow-x: auto;
  max-height: 600px;
}

.pivot-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
  min-width: 600px;
}

.pivot-table th,
.pivot-table td {
  border: 1px solid #dee2e6;
  padding: 10px 12px;
  text-align: left;
}

.pivot-table th {
  background: #f8f9fa;
  font-weight: bold;
  color: #495057;
  position: sticky;
  top: 0;
}

/* En-têtes spécifiques */
.row-header {
  background: #007bff !important;
  color: white !important;
}

.col-header {
  background: #6c757d !important;
  color: white !important;
  text-align: center !important;
}

.value-header {
  background: #28a745 !important;
  color: white !important;
}

.total-header {
  background: #ffc107 !important;
  color: #212529 !important;
}

/* Cellules */
.row-cell {
  background: #f8f9fa;
  font-weight: 600;
  color: #212529;
}

.subtotal-badge {
  font-size: 11px;
  color: #6c757d;
  margin-left: 8px;
  font-style: italic;
}

.data-cell {
  text-align: right;
}

.numeric {
  font-family: 'Consolas', monospace;
  text-align: right;
}

/* Lignes */
.data-row:hover {
  background: #f8f9fa;
}

.subtotal-row {
  background: #e9ecef !important;
  font-weight: bold;
}

.total-cell {
  background: #fff3cd !important;
  font-weight: bold;
}

.grand-total-row {
  background: #343a40 !important;
  color: white !important;
}

.grand-total-label {
  color: white !important;
  font-weight: bold !important;
}

.grand-total-cell {
  background: #ffc107 !important;
  color: #212529 !important;
  font-weight: bold !important;
}

/* État vide */
.empty-state {
  text-align: center;
  padding: 40px;
  color: #6c757d;
  font-style: italic;
}

/* Buttons */
.btn-primary, .btn-secondary {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
}

.btn-primary {
  background: #007bff;
  color: white;
}

.btn-primary:hover {
  background: #0056b3;
}

.btn-secondary {
  background: #6c757d;
  color: white;
}

.btn-secondary:hover {
  background: #545b62;
}

/* Responsive */
@media (max-width: 768px) {
  .config-grid {
    grid-template-columns: 1fr;
  }
  
  .options {
    flex-direction: column;
    gap: 10px;
  }
  
  .results-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
}
</style>